import UserProfileDrawer from "@/components/UserProfileDrawer";
import AISummary from "@/components/AISummary";
import {
  Avatar,
  Box,
  Paper,
  Stack,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useState } from "react";

import { useFrontRow } from "@/services/data";
import SpotifyIcon from "@/fragments/SpotifyIcon";
import TikTokIcon from "@/fragments/TikTokIcon";
import { Instagram } from "@mui/icons-material";
import { daysAgo } from "@/utils/time";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#A78BFA",
    },
    background: {
      default: "#121212",
      paper: "#1E1E1E",
    },
  },
});

export default function FrontRow() {
  const [selectedUser, setSelectedUser] = useState(null);
  const [limit, setLimit] = useState(9);

  const { data, isLoading: loading } = useFrontRow(5000);
  const allUsers = data?.front_row ?? [];
  const displayedUsers = allUsers.slice(0, limit);

  const gridContainerStyle = {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(3, 1fr)",
      sm: "repeat(3, 1fr)",
    },
    gap: 3,
    my: 3,
    overflow: "auto",
    "::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Paper
        elevation={0}
        sx={{
          bgcolor: "background.default",
          borderRadius: 3,
          px: 2,
          py: 4,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            color="primary"
            sx={{
              fontWeight: 600,
              mb: 3,
            }}
          >
            Audience
          </Typography>

          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 5,
              p: 0.5,
              display: "inline-flex",
              mb: 3,
            }}
          >
            <ToggleButtonGroup
              value={limit}
              exclusive
              onChange={(e, newValue) => newValue && setLimit(newValue)}
              aria-label="data window"
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: 4,
                  px: 3,
                  py: 1,
                  color: "grey.400",
                  "&.Mui-selected": {
                    bgcolor: "background.default",
                    color: "white",
                    "&:hover": {
                      bgcolor: "background.default",
                    },
                  },
                },
              }}
            >
              <ToggleButton value={9}>FRONT ROW</ToggleButton>
              <ToggleButton value={5000}>
                30 DAY ACTIVE
                {allUsers.length > 0 && ` (${allUsers.length})`}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {loading && (
            <Box sx={gridContainerStyle}>
              {Array(9)
                .fill(null)
                .map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Skeleton
                      variant="circular"
                      width={80}
                      height={80}
                      sx={{ bgcolor: "background.paper" }}
                    />
                    <Skeleton
                      variant="text"
                      width={60}
                      sx={{ bgcolor: "background.paper" }}
                    />
                  </Box>
                ))}
            </Box>
          )}

          {!loading && allUsers.length === 0 && (
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mt: 2,
                textAlign: "center",
              }}
            >
              No audience members yet
            </Typography>
          )}

          <Box sx={gridContainerStyle}>
            {displayedUsers.map((user, index) => (
              <Box
                key={user.key || index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* <Typography>{user}</Typography> */}
                {/* {console.log(user.created)} */}

                {/* <UserAvatar
                  user={user}
                  onClick={() => setSelectedUser(user)}
                  maxEvents={maxEvents}
                /> */}
                <UserProfileDrawer
                  userKey={user.key}
                  userProfile={user.meta.profile}
                  avatarSize={80}
                />
                <Stack direction="column" spacing={1} alignItems="center">
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {user.name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={daysAgo(user.created) + " days"}
                      size="small"
                      variant="outlined"
                      sx={{
                        opacity: 0.5,
                      }}
                    />
                    {user.meta.instagram && <Instagram sx={{ fontSize: 20 }} />}
                    {user.meta.spotify && <SpotifyIcon size={20} />}
                    {user.meta.tiktok && <TikTokIcon size={20} />}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        {selectedUser && (
          <UserProfileDrawer
            userKey={selectedUser.key}
            open={!!selectedUser}
            setOpen={(open) => !open && setSelectedUser(null)}
          />
        )}
      </Paper>
    </ThemeProvider>
  );
}
